/* ═══════════════════════════════════════════════════════════════
   TREASURE BOX — storefront interactions (Shopify port)
   Vanilla JS, no dependencies. Same UX as the original static site;
   product data is injected from Liquid (window.__TB__) and the cart
   uses Shopify's AJAX Cart API so checkout is real.
   ═══════════════════════════════════════════════════════════════ */
(() => {
  "use strict";

  /* ─── Catalog (injected from Liquid in layout/theme.liquid) ── */
  const TB = window.__TB__ || {};
  const PRODUCTS = TB.products || [];
  const SHOP_URL = TB.shopUrl || "/collections/all";
  const CART_URL = TB.cartUrl || "/cart";
  const SYMBOL = TB.currencySymbol || "R";

  const CAT_ORDER = ["Cookware", "Appliances", "Kitchen Accessories", "Dining & Serving", "Gifts"];
  const BRAND_ORDER = ["KitchenAid", "Guzzini", "Eva Solo", "Saladmaster", "Treasure Box Curated"];
  const present = (order, key) => {
    const found = [...new Set(PRODUCTS.map(p => p[key]).filter(Boolean))];
    return [...order.filter(v => found.includes(v)), ...found.filter(v => !order.includes(v))];
  };
  const CATEGORIES = present(CAT_ORDER, "category");
  const BRANDS = present(BRAND_ORDER, "brand");
  const byId = id => PRODUCTS.find(p => p.id === id);

  /* ─── Tiny helpers ────────────────────────────────────────── */
  const $  = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const money = n => SYMBOL + (Math.round(n * 100) / 100).toLocaleString("en-US", {
    minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2
  });
  const reduceMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const PAGE = document.body.dataset.page || "home";

  const store = {
    get(key, fallback) {
      try { const v = localStorage.getItem("tb_" + key); return v ? JSON.parse(v) : fallback; }
      catch { return fallback; }
    },
    set(key, value) {
      try { localStorage.setItem("tb_" + key, JSON.stringify(value)); } catch { /* private mode */ }
    }
  };

  const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  /* ─── Toasts ──────────────────────────────────────────────── */
  const toastsEl = $("#toasts");
  function toast(msg, icon = "check") {
    const icons = {
      check: '<path d="m5 12.5 4.5 4.5L19 7"/>',
      heart: '<path d="M19.5 12.6 12 20l-7.5-7.4A5 5 0 1 1 12 6.3a5 5 0 1 1 7.5 6.3Z"/>',
      bag:   '<path d="M6 7h12l1.2 13.1a1 1 0 0 1-1 1.09H5.8a1 1 0 0 1-1-1.09L6 7Z"/><path d="M9 10V6a3 3 0 0 1 6 0v4"/>',
      info:  '<circle cx="12" cy="12" r="9"/><path d="M12 8h.01M12 11v5"/>'
    };
    const el = document.createElement("div");
    el.className = "toast";
    el.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${icons[icon] || icons.check}</svg><span>${msg}</span>`;
    toastsEl.appendChild(el);
    setTimeout(() => {
      el.classList.add("out");
      setTimeout(() => el.remove(), 500);
    }, 3400);
  }

  /* ─── Layer manager (drawers / modals / overlays) ─────────── */
  const backdrop = $("#backdrop");
  let openLayers = [];
  let lastFocus = null;

  function openLayer(el, { cls = "open", useBackdrop = true } = {}) {
    lastFocus = document.activeElement;
    el.hidden = false;
    void el.offsetWidth;                       // reflow so the transition runs
    el.classList.add(cls);
    if (useBackdrop) { backdrop.hidden = false; void backdrop.offsetWidth; backdrop.classList.add("show"); }
    document.body.classList.add("locked");
    openLayers.push({ el, cls, useBackdrop });
    const first = el.querySelector(FOCUSABLE);
    if (first) setTimeout(() => first.focus({ preventScroll: true }), 60);
    el.addEventListener("keydown", trapFocus);
  }

  function closeLayer(el) {
    const idx = openLayers.findIndex(l => l.el === el);
    if (idx === -1) return;
    const { cls, useBackdrop } = openLayers[idx];
    openLayers.splice(idx, 1);
    el.classList.remove(cls);
    el.removeEventListener("keydown", trapFocus);
    setTimeout(() => { el.hidden = true; }, 560);
    if (useBackdrop && !openLayers.some(l => l.useBackdrop)) {
      backdrop.classList.remove("show");
      setTimeout(() => { backdrop.hidden = true; }, 450);
    }
    if (!openLayers.length) document.body.classList.remove("locked");
    if (lastFocus) { lastFocus.focus({ preventScroll: true }); lastFocus = null; }
  }

  function closeTopLayer() {
    if (openLayers.length) closeLayer(openLayers[openLayers.length - 1].el);
  }

  function trapFocus(e) {
    if (e.key !== "Tab") return;
    const el = e.currentTarget;
    const items = $$(FOCUSABLE, el).filter(i => i.offsetParent !== null);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
  }

  document.addEventListener("keydown", e => { if (e.key === "Escape") closeTopLayer(); });
  backdrop.addEventListener("click", closeTopLayer);

  /* ─── Image skeletons ─────────────────────────────────────── */
  function watchImages(scope = document) {
    $$(".img-wrap img", scope).forEach(img => {
      const wrap = img.closest(".img-wrap");
      const done = () => wrap.classList.add("loaded");
      if (img.complete && img.naturalWidth) done();
      else {
        img.addEventListener("load", done, { once: true });
        img.addEventListener("error", done, { once: true });
      }
    });
  }

  /* ─── Product card builder ────────────────────────────────── */
  const starsHtml = (rating) =>
    `<div class="stars" role="img" aria-label="Rated ${rating} out of 5 stars"><span style="width:${(rating / 5) * 100}%"></span></div>`;

  const productCardHTML = (p, i = 0) => `
    <article class="product-card reveal" style="--d:${Math.min(i % 4 * 0.07, 0.28)}s" role="listitem" data-id="${p.id}" data-brand="${p.brand}" data-category="${p.category}">
      <div class="product-media img-wrap" data-quickview="${p.id}" role="button" tabindex="0" aria-label="Quick view ${p.name}">
        <img loading="lazy" decoding="async" width="640" height="736" src="${p.img}" alt="${p.name}" />
        ${p.tag ? `<span class="product-tag ${p.sale ? "product-tag--sale" : ""}">${p.tag}</span>` : ""}
        <button class="product-wish" data-wish="${p.id}" aria-label="Save ${p.name} to wishlist" aria-pressed="false">
          <svg viewBox="0 0 24 24"><path d="M19.5 12.6 12 20l-7.5-7.4A5 5 0 1 1 12 6.3a5 5 0 1 1 7.5 6.3Z"/></svg>
        </button>
        <button class="product-quickview" data-quickview="${p.id}">Quick View</button>
      </div>
      <div class="product-info">
        <p class="product-brand">${p.brand}</p>
        <h3 class="product-name"><button data-quickview="${p.id}">${p.name}</button></h3>
        <div class="rating-row">${starsHtml(p.rating)}<span>${p.rating} (${p.reviews})</span></div>
        <div class="price-row">
          <span class="price">${money(p.price)}</span>
          ${p.oldPrice ? `<s class="price-old">${money(p.oldPrice)}</s>` : ""}
        </div>
        <p class="product-social">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"/><circle cx="12" cy="12" r="3"/></svg>
          ${p.viewsToday} people viewed this today
        </p>
        <button class="product-add" data-add="${p.id}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M6 7h12l1.2 13.1a1 1 0 0 1-1 1.09H5.8a1 1 0 0 1-1-1.09L6 7Z"/><path d="M9 10V6a3 3 0 0 1 6 0v4"/></svg>
          Quick Add
        </button>
      </div>
    </article>`;

  /* ─── Best-sellers rail (home) ────────────────────────────── */
  const rail = $("#productRail");
  if (rail) {
    rail.innerHTML = PRODUCTS.filter(p => p.featured).map((p, i) => productCardHTML(p, i)).join("");
    watchImages(rail);
    const railStep = () => (rail.firstElementChild ? rail.firstElementChild.getBoundingClientRect().width + 20 : 320);
    $("#railPrev").addEventListener("click", () => rail.scrollBy({ left: -railStep(), behavior: "smooth" }));
    $("#railNext").addEventListener("click", () => rail.scrollBy({ left: railStep(), behavior: "smooth" }));
  }

  /* ─── Shop page: filterable catalog ───────────────────────── */
  const shopGrid = $("#shopGrid");
  const shopState = { category: "All", brand: "All", sort: "featured" };

  function readShopParams() {
    const q = new URLSearchParams(location.search);
    const cat = q.get("category"), br = q.get("brand"), so = q.get("sort");
    if (cat && (cat === "All" || CATEGORIES.includes(cat))) shopState.category = cat;
    if (br && (br === "All" || BRANDS.includes(br))) shopState.brand = br;
    if (so) shopState.sort = so;
  }

  function writeShopParams() {
    try {
      const q = new URLSearchParams();
      if (shopState.category !== "All") q.set("category", shopState.category);
      if (shopState.brand !== "All") q.set("brand", shopState.brand);
      if (shopState.sort !== "featured") q.set("sort", shopState.sort);
      const url = location.pathname + (q.toString() ? "?" + q.toString() : "");
      history.replaceState(null, "", url);
    } catch { /* sandboxed previews etc. */ }
  }

  function shopResults() {
    let list = PRODUCTS.filter(p =>
      (shopState.category === "All" || p.category === shopState.category) &&
      (shopState.brand === "All" || p.brand === shopState.brand));
    switch (shopState.sort) {
      case "price-asc":  list = [...list].sort((a, b) => a.price - b.price); break;
      case "price-desc": list = [...list].sort((a, b) => b.price - a.price); break;
      case "rating":     list = [...list].sort((a, b) => b.rating - a.rating); break;
      default:           list = [...list].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }
    return list;
  }

  function renderShop() {
    if (!shopGrid) return;
    const list = shopResults();

    /* chips + selects reflect state */
    $$("#shopChips .chip").forEach(c =>
      c.setAttribute("aria-pressed", String(c.dataset.filterCategory === shopState.category)));
    const brandSel = $("#brandSelect"), sortSel = $("#sortSelect");
    if (brandSel) brandSel.value = shopState.brand;
    if (sortSel) sortSel.value = shopState.sort;

    $("#shopCount").textContent = list.length
      ? `${list.length} treasure${list.length > 1 ? "s" : ""}`
      : "No matches";

    if (!list.length) {
      shopGrid.innerHTML = `
        <div class="shop-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.2-3.2"/></svg>
          <p>Nothing in <strong>${shopState.category}</strong> from <strong>${shopState.brand}</strong> just yet.<br/>New treasures arrive every month.</p>
          <button class="btn btn-primary" id="shopReset">Show everything</button>
        </div>`;
      $("#shopReset").addEventListener("click", () => { shopState.category = "All"; shopState.brand = "All"; renderShop(); });
    } else {
      shopGrid.innerHTML = list.map((p, i) => productCardHTML(p, i)).join("");
    }
    watchImages(shopGrid);
    syncWishButtons();
    observeReveals();
    writeShopParams();
  }

  if (shopGrid) {
    /* preset filter from the Shopify collection being viewed, then URL params */
    const shopMeta = $("#shopMeta");
    if (shopMeta && shopMeta.dataset.handle && shopMeta.dataset.handle !== "all") {
      const t = shopMeta.dataset.title;
      if (CATEGORIES.includes(t)) shopState.category = t;
      else if (BRANDS.includes(t)) shopState.brand = t;
    }
    readShopParams();

    /* build category chips */
    $("#shopChips").innerHTML = ["All", ...CATEGORIES].map(c =>
      `<button class="chip chip--filter" data-filter-category="${c}" aria-pressed="false">${c}</button>`).join("");
    $("#shopChips").addEventListener("click", e => {
      const chip = e.target.closest("[data-filter-category]");
      if (!chip) return;
      shopState.category = chip.dataset.filterCategory;
      renderShop();
    });

    /* brand + sort selects */
    $("#brandSelect").innerHTML = ["All", ...BRANDS].map(b =>
      `<option value="${b}">${b === "All" ? "All brands" : b}</option>`).join("");
    $("#brandSelect").addEventListener("change", e => { shopState.brand = e.target.value; renderShop(); });
    $("#sortSelect").addEventListener("change", e => { shopState.sort = e.target.value; renderShop(); });
    /* initial render happens in Init, after cart/wishlist state exists */
  }

  /* ─── Cart (Shopify AJAX Cart API) ────────────────────────── */
  const FREE_SHIP = TB.freeShip || 150;
  let cart = {};            // { handle: qty } — UI mirror of the Shopify cart
  let cartLineKeys = {};    // { handle: line key } for /cart/change.js
  let cartSubtotal = 0;

  const variantToHandle = {};
  PRODUCTS.forEach(p => { variantToHandle[p.variantId] = p.id; });

  const cartDrawer = $("#cartDrawer");
  const cartBody = $("#cartBody");
  const cartCountEls = [$("#cartCount"), $("#floatCartCount")];
  const floatCart = $("#floatCart");

  const cartQty = () => Object.values(cart).reduce((a, b) => a + b, 0);
  const cartTotal = () => cartSubtotal;

  function syncCart(c) {
    cart = {}; cartLineKeys = {};
    (c.items || []).forEach(it => {
      const h = variantToHandle[it.variant_id] || it.handle;
      cart[h] = (cart[h] || 0) + it.quantity;
      cartLineKeys[h] = it.key;
    });
    cartSubtotal = (c.total_price || 0) / 100;
    refreshCartUI();
  }

  async function loadCart() {
    try {
      const r = await fetch(CART_URL + ".js", { headers: { Accept: "application/json" } });
      if (r.ok) syncCart(await r.json());
    } catch { refreshCartUI(); }
  }

  async function cartAddLine(handle, qty) {
    const p = byId(handle);
    if (!p) return false;
    try {
      const r = await fetch(CART_URL + "/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: p.variantId, quantity: qty }] })
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast(err.description || err.message || "We couldn't add that just now — please try again.", "info");
        return false;
      }
      await loadCart();
      return true;
    } catch {
      toast("We couldn't reach the bag — please try again.", "info");
      return false;
    }
  }

  async function cartSetLine(handle, qty) {
    const key = cartLineKeys[handle];
    if (!key) return;
    try {
      const r = await fetch(CART_URL + "/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity: Math.max(0, qty) })
      });
      if (r.ok) syncCart(await r.json()); else loadCart();
    } catch { loadCart(); }
    if (PAGE === "cart") location.reload();
  }

  function refreshCartUI() {
    const qty = cartQty();
    cartCountEls.forEach(el => { el.textContent = qty; });
    $("#cartCount").hidden = qty === 0;
    floatCart.hidden = qty === 0;
    $("#cartHeadCount").textContent = qty ? `· ${qty} item${qty > 1 ? "s" : ""}` : "";

    /* free-shipping progress */
    const total = cartTotal();
    const pct = Math.min(100, (total / FREE_SHIP) * 100);
    $("#shipProgressBar").style.width = pct + "%";
    $("#shipProgressLabel").innerHTML = total >= FREE_SHIP
      ? "<strong>Free shipping</strong> unlocked — enjoy!"
      : `You're <strong>${money(Math.max(0, FREE_SHIP - total))}</strong> away from free shipping`;

    /* items */
    if (!qty) {
      cartBody.innerHTML = `
        <div class="drawer-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 7h12l1.2 13.1a1 1 0 0 1-1 1.09H5.8a1 1 0 0 1-1-1.09L6 7Z"/><path d="M9 10V6a3 3 0 0 1 6 0v4"/></svg>
          <p>Your bag is empty —<br/>let's find something beautiful.</p>
          ${PAGE === "shop"
            ? '<button class="btn btn-primary" data-close-drawer>Keep browsing</button>'
            : `<a class="btn btn-primary" href="${SHOP_URL}">Browse the collection</a>`}
        </div>`;
      $("#cartFoot").style.display = "none";
    } else {
      $("#cartFoot").style.display = "";
      cartBody.innerHTML = Object.entries(cart).map(([id, q]) => {
        const p = byId(id); if (!p) return "";
        return `
        <div class="cart-item" data-id="${id}">
          <img src="${p.img}" alt="${p.name}" width="82" height="82" loading="lazy" />
          <div>
            <p class="ci-brand">${p.brand}</p>
            <h4>${p.name}</h4>
            <p class="ci-price">${money(p.price)}</p>
          </div>
          <div class="ci-controls">
            <button class="ci-remove" data-remove="${id}" aria-label="Remove ${p.name}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
            <div class="qty" aria-label="Quantity of ${p.name}">
              <button data-dec="${id}" aria-label="Decrease">−</button>
              <span aria-live="polite">${q}</span>
              <button data-inc="${id}" aria-label="Increase">+</button>
            </div>
          </div>
        </div>`;
      }).join("");
    }
    $("#cartSubtotal").textContent = money(total);
  }

  async function addToCart(id, qty = 1, sourceImg = null) {
    const p = byId(id);
    if (!p) return;
    const ok = await cartAddLine(id, qty);
    if (!ok) return;
    cartCountEls.forEach(el => { el.classList.remove("bump"); void el.offsetWidth; el.classList.add("bump"); });
    if (sourceImg && !reduceMotion) flyToCart(sourceImg);
    toast(`Added to your bag — ${p.name.split("·")[0].trim()}`, "bag");
  }

  function flyToCart(img) {
    const target = ($("#cartOpen").offsetParent ? $("#cartOpen") : floatCart).getBoundingClientRect();
    const from = img.getBoundingClientRect();
    const ghost = img.cloneNode();
    ghost.className = "fly-img";
    ghost.style.left = from.left + from.width / 2 - 38 + "px";
    ghost.style.top = from.top + from.height / 2 - 38 + "px";
    document.body.appendChild(ghost);
    void ghost.offsetWidth;
    const dx = target.left + target.width / 2 - (from.left + from.width / 2);
    const dy = target.top + target.height / 2 - (from.top + from.height / 2);
    ghost.style.transform = `translate(${dx}px, ${dy}px) scale(.15)`;
    ghost.style.opacity = "0.25";
    setTimeout(() => ghost.remove(), 900);
  }

  $("#cartOpen").addEventListener("click", () => openLayer(cartDrawer));
  floatCart.addEventListener("click", () => openLayer(cartDrawer));
  $("#checkoutBtn").addEventListener("click", () => {
    window.location.href = CART_URL + "/checkout";
  });

  /* ─── Wishlist ────────────────────────────────────────────── */
  let wishlist = store.get("wish", []);
  const wishlistDrawer = $("#wishlistDrawer");

  function saveWish() { store.set("wish", wishlist); refreshWishUI(); }

  function refreshWishUI() {
    const n = wishlist.length;
    const badge = $("#wishlistCount");
    badge.textContent = n; badge.hidden = n === 0;
    if (n) { badge.classList.remove("bump"); void badge.offsetWidth; badge.classList.add("bump"); }

    $("#wishlistBody").innerHTML = n ? wishlist.map(id => {
      const p = byId(id); if (!p) return "";
      return `
      <div class="cart-item" data-id="${id}">
        <img src="${p.img}" alt="${p.name}" width="82" height="82" loading="lazy" />
        <div>
          <p class="ci-brand">${p.brand}</p>
          <h4>${p.name}</h4>
          <p class="ci-price">${money(p.price)}</p>
        </div>
        <div class="ci-controls">
          <button class="ci-remove" data-unwish="${id}" aria-label="Remove ${p.name} from wishlist">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
          <button class="btn-text" data-add="${id}">To bag
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14m-6-6 6 6-6 6"/></svg>
          </button>
        </div>
      </div>`;
    }).join("") : `
      <div class="drawer-empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="M19.5 12.6 12 20l-7.5-7.4A5 5 0 1 1 12 6.3a5 5 0 1 1 7.5 6.3Z"/></svg>
        <p>Nothing saved yet.<br/>Tap the heart on any piece you love — we'll keep it safe here.</p>
      </div>`;
    syncWishButtons();
  }

  function toggleWish(id) {
    const on = wishlist.includes(id);
    wishlist = on ? wishlist.filter(w => w !== id) : [...wishlist, id];
    saveWish();
    toast(on ? "Removed from your wishlist" : "Saved to your wishlist ♡", "heart");
  }

  function syncWishButtons() {
    $$("[data-wish]").forEach(btn => {
      const on = wishlist.includes(btn.dataset.wish);
      btn.classList.toggle("active", on);
      btn.setAttribute("aria-pressed", String(on));
    });
    const qvWish = $("#qvWish");
    if (qvWish.dataset.id) {
      const on = wishlist.includes(qvWish.dataset.id);
      qvWish.classList.toggle("active", on);
      qvWish.setAttribute("aria-pressed", String(on));
    }
  }

  $("#wishlistOpen").addEventListener("click", () => openLayer(wishlistDrawer));

  /* ─── Recently viewed ─────────────────────────────────────── */
  let recent = store.get("recent", []);
  function recordView(id) {
    recent = [id, ...recent.filter(r => r !== id)].slice(0, 6);
    store.set("recent", recent);
    renderRecent();
  }
  function renderRecent() {
    const section = $("#recentlyViewed");
    if (!section) return;
    const row = $("#recentRow");
    const items = recent.map(byId).filter(Boolean);
    section.hidden = items.length === 0;
    row.innerHTML = items.map(p => `
      <button class="recent-card" data-quickview="${p.id}" aria-label="Quick view ${p.name}">
        <span class="img-wrap"><img loading="lazy" decoding="async" src="${p.img}" alt="" width="190" height="190" /></span>
        <p>${p.name}</p>
        <span>${money(p.price)}</span>
      </button>`).join("");
    watchImages(row);
  }
  $("#recentClear")?.addEventListener("click", () => {
    recent = []; store.set("recent", recent); renderRecent();
    toast("Recently viewed cleared");
  });

  /* ─── Quick view modal ────────────────────────────────────── */
  const quickView = $("#quickView");
  let qvProduct = null, qvQty = 1;

  function openQuickView(id) {
    const p = byId(id); if (!p) return;
    qvProduct = p; qvQty = 1;
    $("#qvQty").textContent = "1";
    $("#qvBrand").textContent = p.brand;
    $("#qvName").textContent = p.name;
    $("#qvStars").outerHTML = starsHtml(p.rating).replace('class="stars"', 'class="stars" id="qvStars"');
    $("#qvReviews").textContent = `${p.rating} · ${p.reviews} reviews`;
    $("#qvPrice").textContent = money(p.price);
    const old = $("#qvOldPrice");
    old.hidden = !p.oldPrice;
    if (p.oldPrice) old.textContent = money(p.oldPrice);
    $("#qvDesc").textContent = p.desc;
    $("#qvSocialText").textContent = `${p.viewsToday} people viewed this today · only a few left`;
    const wrap = $(".qv-media");
    wrap.classList.remove("loaded");
    const img = $("#qvImg");
    img.src = p.img; img.alt = p.name;
    watchImages(quickView);
    $("#qvWish").dataset.id = p.id;
    syncWishButtons();

    $("#qvPairs").innerHTML = (p.pairs || []).map(pid => {
      const pp = byId(pid); if (!pp) return "";
      return `<button class="qv-pair" data-quickview="${pid}">
        <img src="${pp.img}" alt="" width="38" height="38" loading="lazy" />
        <span>${pp.name.split("·")[0].trim()} · ${money(pp.price)}</span>
      </button>`;
    }).join("");

    recordView(id);
    openLayer(quickView);
  }

  $("#quickViewClose").addEventListener("click", () => closeLayer(quickView));
  $("#exitClose").addEventListener("click", () => closeLayer($("#exitPopup")));
  $("#qvMinus").addEventListener("click", () => { qvQty = Math.max(1, qvQty - 1); $("#qvQty").textContent = qvQty; });
  $("#qvPlus").addEventListener("click", () => { qvQty = Math.min(9, qvQty + 1); $("#qvQty").textContent = qvQty; });
  $("#qvAdd").addEventListener("click", () => {
    if (!qvProduct) return;
    addToCart(qvProduct.id, qvQty, $("#qvImg"));
    closeLayer(quickView);
    setTimeout(() => openLayer(cartDrawer), 620);
  });
  $("#qvWish").addEventListener("click", e => { if (e.currentTarget.dataset.id) toggleWish(e.currentTarget.dataset.id); });

  /* ─── Product page (Shopify product template) ─────────────── */
  const pdPage = $("#tbProductPage");
  if (pdPage) {
    const handle = pdPage.dataset.handle;
    recordView(handle);
    let pdQty = 1;
    const pdQtyEl = $("#pdQty");
    $("#pdMinus")?.addEventListener("click", () => { pdQty = Math.max(1, pdQty - 1); pdQtyEl.textContent = pdQty; });
    $("#pdPlus")?.addEventListener("click", () => { pdQty = Math.min(9, pdQty + 1); pdQtyEl.textContent = pdQty; });
    $("#pdAdd")?.addEventListener("click", () => {
      addToCart(handle, pdQty, $("#pdImg"));
      setTimeout(() => openLayer(cartDrawer), 620);
    });
  }

  /* ─── Global click delegation ─────────────────────────────── */
  document.addEventListener("click", e => {
    const t = e.target;

    /* specific controls first — several live inside a [data-quickview] tile */
    const wish = t.closest("[data-wish]");
    if (wish) { toggleWish(wish.dataset.wish); return; }

    const add = t.closest("[data-add]");
    if (add) {
      const card = add.closest(".product-card, .cart-item");
      const img = card ? card.querySelector("img") : null;
      addToCart(add.dataset.add, 1, img);
      add.classList.add("added");
      setTimeout(() => add.classList.remove("added"), 1200);
      return;
    }
    const qv = t.closest("[data-quickview]");
    if (qv) {
      e.preventDefault();
      const openFrom = qv.closest(".modal");
      if (openFrom) closeLayer(openFrom);
      setTimeout(() => openQuickView(qv.dataset.quickview), openFrom ? 480 : 0);
      return;
    }
    const unwish = t.closest("[data-unwish]");
    if (unwish) { wishlist = wishlist.filter(w => w !== unwish.dataset.unwish); saveWish(); return; }

    const inc = t.closest("[data-inc]");
    if (inc) { cartSetLine(inc.dataset.inc, (cart[inc.dataset.inc] || 0) + 1); return; }
    const dec = t.closest("[data-dec]");
    if (dec) { cartSetLine(dec.dataset.dec, (cart[dec.dataset.dec] || 0) - 1); return; }
    const rm = t.closest("[data-remove]");
    if (rm) { cartSetLine(rm.dataset.remove, 0); return; }

    const closer = t.closest("[data-close-drawer]");
    if (closer) {
      const layer = closer.closest(".drawer, .modal");
      if (layer) closeLayer(layer);
      if (closer.dataset.goto) setTimeout(() => $(closer.dataset.goto)?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth" }), 380);
      return;
    }
    const soon = t.closest("[data-soon]");
    if (soon) { e.preventDefault(); toast("This page is on its way — WhatsApp us meanwhile!", "info"); return; }
  });

  /* keyboard access for the quick-view media tile */
  document.addEventListener("keydown", e => {
    if ((e.key === "Enter" || e.key === " ") && e.target.matches?.(".product-media[data-quickview]")) {
      e.preventDefault();
      openQuickView(e.target.dataset.quickview);
    }
  });

  /* ─── Search ──────────────────────────────────────────────── */
  const searchOverlay = $("#searchOverlay");
  const searchInput = $("#searchInput");
  const searchResults = $("#searchResults");
  const POPULAR = ["Stand mixer", "Serving bowls", "Cookware set", "Eva Solo", "Gifts"];
  let recentSearches = store.get("searches", []);
  let searchSel = -1;

  const INDEX = [
    ...PRODUCTS.map(p => ({ type: "Product", label: p.name, sub: `${p.brand} · ${p.category}`, img: p.img, price: p.price, action: () => openQuickView(p.id) })),
    ...CATEGORIES.map(c =>
      ({ type: "Category", label: c, sub: "Category · browse all", action: () => { location.href = SHOP_URL + "?category=" + encodeURIComponent(c); } })),
    ...BRANDS.map(b =>
      ({ type: "Brand", label: b, sub: "Brand · browse all", action: () => { location.href = SHOP_URL + "?brand=" + encodeURIComponent(b); } }))
  ];

  function renderChips() {
    const chips = [...recentSearches.map(s => ({ s, recent: true })), ...POPULAR.map(s => ({ s }))].slice(0, 7);
    $("#searchChips").innerHTML = chips.map(({ s, recent: r }) =>
      `<button class="chip" data-chip="${s}">${r ? "↺ " : ""}${s}</button>`).join("");
    $(".search-hints .search-label").textContent = recentSearches.length ? "Recent & popular" : "Popular right now";
  }

  function runSearch(q) {
    searchSel = -1;
    const query = q.trim().toLowerCase();
    if (!query) { searchResults.innerHTML = ""; $("#searchHints").hidden = false; return; }
    $("#searchHints").hidden = true;
    const hits = INDEX.filter(i => (i.label + " " + i.sub).toLowerCase().includes(query)).slice(0, 6);
    if (!hits.length) {
      searchResults.innerHTML = `<li class="search-empty">No matches for "<strong>${q}</strong>" — try "mixer" or "bowls", or WhatsApp us, we love a treasure hunt.</li>`;
      return;
    }
    const mark = label => label.replace(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "i"), "<mark>$1</mark>");
    searchResults.innerHTML = hits.map((h, i) => `
      <li><button class="search-result" role="option" data-result="${i}" aria-selected="false">
        ${h.img ? `<img src="${h.img}" alt="" loading="lazy" />`
                : `<span class="search-result-icon" style="width:52px;height:52px;border-radius:10px;background:var(--gold-soft);display:grid;place-items:center;flex:none">
                     <svg style="width:20px;height:20px;color:var(--gold-deep)" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.2-3.2"/></svg></span>`}
        <span style="min-width:0"><strong>${mark(h.label)}</strong><span>${h.sub}</span></span>
        ${h.price ? `<span class="sr-price">${money(h.price)}</span>` : ""}
      </button></li>`).join("");
    searchResults._hits = hits;
  }

  function chooseResult(i) {
    const hits = searchResults._hits || [];
    const hit = hits[i] || hits[0];
    if (!hit) return;
    const q = searchInput.value.trim();
    if (q) {
      recentSearches = [q, ...recentSearches.filter(s => s.toLowerCase() !== q.toLowerCase())].slice(0, 4);
      store.set("searches", recentSearches);
    }
    closeLayer(searchOverlay);
    setTimeout(() => hit.action(), 420);
  }

  $("#searchOpen").addEventListener("click", () => {
    renderChips();
    searchInput.value = ""; runSearch("");
    openLayer(searchOverlay, { useBackdrop: false });
  });
  $("#searchClose").addEventListener("click", () => closeLayer(searchOverlay));
  searchOverlay.addEventListener("click", e => { if (e.target === searchOverlay) closeLayer(searchOverlay); });
  searchInput.addEventListener("input", () => runSearch(searchInput.value));
  searchInput.addEventListener("keydown", e => {
    const options = $$(".search-result", searchResults);
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!options.length) return;
      searchSel = e.key === "ArrowDown"
        ? (searchSel + 1) % options.length
        : (searchSel - 1 + options.length) % options.length;
      options.forEach((o, i) => o.setAttribute("aria-selected", String(i === searchSel)));
      options[searchSel].scrollIntoView({ block: "nearest" });
    } else if (e.key === "Enter") {
      e.preventDefault();
      chooseResult(searchSel);
    }
  });
  searchResults.addEventListener("click", e => {
    const r = e.target.closest("[data-result]");
    if (r) chooseResult(+r.dataset.result);
  });
  $("#searchChips").addEventListener("click", e => {
    const chip = e.target.closest("[data-chip]");
    if (chip) { searchInput.value = chip.dataset.chip; runSearch(chip.dataset.chip); searchInput.focus(); }
  });
  /* “/” opens search, like the pros */
  document.addEventListener("keydown", e => {
    if (e.key === "/" && !openLayers.length && !/input|textarea|select/i.test(document.activeElement.tagName)) {
      e.preventDefault(); $("#searchOpen").click();
    }
  });

  /* ─── Header behavior ─────────────────────────────────────── */
  const header = $("#header");
  let lastY = 0;
  function onScrollHeader() {
    const y = scrollY;
    header.classList.toggle("scrolled", y > 24 || PAGE !== "home");
    if (!openLayers.length) {
      if (y > 640 && y > lastY + 8) header.classList.add("tucked");
      else if (y < lastY - 8 || y < 640) header.classList.remove("tucked");
    }
    lastY = y;
  }

  /* ─── Announcement banner ─────────────────────────────────── */
  const announce = $("#announce");
  if (store.get("announce_dismissed", false)) announce.classList.add("hidden");
  $("#announceClose").addEventListener("click", () => {
    announce.classList.add("hidden");
    store.set("announce_dismissed", true);
  });

  /* ─── Mobile menu ─────────────────────────────────────────── */
  const burger = $("#navBurger");
  const mobileMenu = $("#mobileMenu");
  const scrim = $("#scrim");
  function toggleMenu(force) {
    const open = force ?? mobileMenu.hidden;
    burger.setAttribute("aria-expanded", String(open));
    if (open) {
      mobileMenu.hidden = false; scrim.hidden = false;
      void mobileMenu.offsetWidth;
      mobileMenu.classList.add("open"); scrim.classList.add("show");
      document.body.classList.add("locked");
    } else {
      mobileMenu.classList.remove("open"); scrim.classList.remove("show");
      setTimeout(() => { mobileMenu.hidden = true; scrim.hidden = true; }, 520);
      document.body.classList.remove("locked");
    }
  }
  burger.addEventListener("click", () => toggleMenu());
  scrim.addEventListener("click", () => toggleMenu(false));
  mobileMenu.addEventListener("click", e => { if (e.target.closest("a")) toggleMenu(false); });
  document.addEventListener("keydown", e => { if (e.key === "Escape" && !mobileMenu.hidden) toggleMenu(false); });

  /* ─── Scrollspy (home sections) ───────────────────────────── */
  if (PAGE === "home") {
    const spy = new IntersectionObserver(entries => {
      entries.forEach(en => {
        if (en.isIntersecting) {
          $$(".nav a").forEach(a => a.classList.toggle("active", a.getAttribute("href") === "#" + en.target.id));
        }
      });
    }, { rootMargin: "-40% 0px -55% 0px" });
    ["bestsellers", "brands", "categories", "testimonials"].forEach(id => {
      const el = document.getElementById(id); if (el) spy.observe(el);
    });
  }

  /* ─── Reveal on scroll ────────────────────────────────────── */
  /* Driven by the rAF scroll loop (not IntersectionObserver alone)
     so content still appears in throttled/background tabs. */
  function observeReveals() {
    const vh = innerHeight;
    $$(".reveal:not(.in)").forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.top < vh * 0.96 && r.bottom > 0) el.classList.add("in");
    });
  }
  addEventListener("resize", observeReveals, { passive: true });
  document.addEventListener("visibilitychange", () => { if (!document.hidden) observeReveals(); });

  /* ─── Parallax (hero + quote band, home only) ─────────────── */
  const heroMedia = $("#heroMedia");
  const quoteMedia = $("#quoteMedia");
  const quoteBand = $(".quote-band");
  let ticking = false;
  function parallax() {
    ticking = false;
    observeReveals();
    if (reduceMotion) return;
    if (heroMedia) heroMedia.style.transform = `translateY(${Math.min(scrollY, innerHeight) * 0.22}px)`;
    if (quoteBand) {
      const r = quoteBand.getBoundingClientRect();
      if (r.bottom > 0 && r.top < innerHeight) {
        const mid = r.top + r.height / 2 - innerHeight / 2;
        quoteMedia.style.transform = `translateY(${mid * -0.14}px)`;
      }
    }
  }
  addEventListener("scroll", () => {
    onScrollHeader();
    if (!ticking) { requestAnimationFrame(parallax); ticking = true; }
  }, { passive: true });

  /* ─── Testimonials carousel (home only) ───────────────────── */
  const track = $("#carouselTrack");
  if (track) {
    const slides = $$(".testimonial", track);
    const dotsBox = $("#carouselDots");
    let current = 0, autoTimer = null;

    slides.forEach((_, i) => {
      const b = document.createElement("button");
      b.setAttribute("role", "tab");
      b.setAttribute("aria-label", `Testimonial ${i + 1}`);
      b.setAttribute("aria-selected", String(i === 0));
      b.addEventListener("click", () => { goTo(i); restartAuto(); });
      dotsBox.appendChild(b);
    });
    const dots = $$("button", dotsBox);

    function goTo(i) {
      const next = (i + slides.length) % slides.length;
      if (next === current) return;
      slides[current].classList.add("leaving");
      slides[current].classList.remove("current");
      slides[current].setAttribute("aria-hidden", "true");
      const old = current; current = next;
      setTimeout(() => slides[old].classList.remove("leaving"), 700);
      slides[current].classList.add("current");
      slides[current].setAttribute("aria-hidden", "false");
      dots.forEach((d, di) => d.setAttribute("aria-selected", String(di === current)));
    }
    slides[0].classList.add("current");

    function restartAuto() {
      clearInterval(autoTimer);
      if (!reduceMotion) autoTimer = setInterval(() => goTo(current + 1), 6500);
    }
    $("#carouselPrev").addEventListener("click", () => { goTo(current - 1); restartAuto(); });
    $("#carouselNext").addEventListener("click", () => { goTo(current + 1); restartAuto(); });
    const carousel = $("#carousel");
    carousel.addEventListener("mouseenter", () => clearInterval(autoTimer));
    carousel.addEventListener("mouseleave", restartAuto);
    document.addEventListener("visibilitychange", () => document.hidden ? clearInterval(autoTimer) : restartAuto());
    /* swipe */
    let swipeX = null;
    track.addEventListener("pointerdown", e => { swipeX = e.clientX; }, { passive: true });
    track.addEventListener("pointerup", e => {
      if (swipeX === null) return;
      const dx = e.clientX - swipeX;
      if (Math.abs(dx) > 46) { goTo(current + (dx < 0 ? 1 : -1)); restartAuto(); }
      swipeX = null;
    }, { passive: true });
    restartAuto();
  }

  /* ─── Newsletter (posts to Shopify customer form) ─────────── */
  const newsForm = $("#newsletterForm");
  if (newsForm) {
    const newsMsg = $("#newsletterMsg");
    newsForm.addEventListener("submit", e => {
      e.preventDefault();
      const email = $("#newsletterEmail").value.trim();
      const btn = $(".btn", newsForm);
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        newsMsg.textContent = "Hmm, that email doesn't look right — mind checking it?";
        newsMsg.classList.add("error");
        $("#newsletterEmail").focus();
        return;
      }
      newsMsg.classList.remove("error");
      btn.classList.add("loading"); btn.disabled = true;

      /* register the subscriber as a Shopify customer (best effort) */
      const fd = new URLSearchParams();
      fd.set("form_type", "customer");
      fd.set("utf8", "✓");
      fd.set("contact[email]", email);
      fd.set("contact[tags]", "newsletter");
      fetch("/contact", { method: "POST", body: fd }).catch(() => { /* offline etc. */ });

      setTimeout(() => {
        btn.classList.remove("loading"); btn.disabled = false;
        newsForm.reset();
        store.set("subscribed", true);
        newsMsg.textContent = "Welcome to the family! Your 10% code: TREASURE10 ✦";
        toast("Subscribed — code TREASURE10 is yours", "check");
      }, 900);
    });
  }

  /* ─── Exit-intent popup ───────────────────────────────────── */
  const exitPopup = $("#exitPopup");
  let exitArmed = false;
  setTimeout(() => { exitArmed = true; }, 7000);
  document.addEventListener("mouseout", e => {
    if (!exitArmed || e.relatedTarget || e.clientY > 24) return;
    if (store.get("exit_shown", false) || store.get("subscribed", false) || openLayers.length) return;
    store.set("exit_shown", true);
    openLayer(exitPopup);
  });
  $("#exitDismiss").addEventListener("click", () => closeLayer(exitPopup));
  $("#promoCopy").addEventListener("click", async () => {
    try { await navigator.clipboard.writeText("TREASURE10"); toast("Code TREASURE10 copied — happy shopping!"); }
    catch { toast("Code: TREASURE10 — jot it down!", "info"); }
    closeLayer(exitPopup);
  });

  /* ─── Custom cursor ───────────────────────────────────────── */
  if (matchMedia("(hover: hover) and (pointer: fine)").matches && !reduceMotion) {
    const dot = $(".cursor-dot"), ring = $(".cursor-ring");
    let mx = -100, my = -100, rx = -100, ry = -100;
    addEventListener("mousemove", e => {
      mx = e.clientX; my = e.clientY;
      dot.style.transform = `translate(${mx}px, ${my}px) translate(-50%, -50%)`;
      document.body.classList.remove("cursor-hidden");
      document.body.classList.toggle("cursor-active",
        !!e.target.closest("a, button, [role='button'], input, select, .product-media, .chip"));
    }, { passive: true });
    document.addEventListener("mouseleave", () => document.body.classList.add("cursor-hidden"));
    (function lerpRing() {
      rx += (mx - rx) * 0.16; ry += (my - ry) * 0.16;
      ring.style.transform = `translate(${rx}px, ${ry}px) translate(-50%, -50%)`;
      requestAnimationFrame(lerpRing);
    })();
  }

  /* ─── Personal welcome back ───────────────────────────────── */
  const visited = store.get("visited", false);
  store.set("visited", true);
  if (visited && PAGE === "home" && wishlist.length > 0) {
    setTimeout(() => toast("Welcome back — your wishlist is waiting ♡", "heart"), 1800);
  }

  /* ─── Init ────────────────────────────────────────────────── */
  const yearEl = $("#year");
  if (yearEl) yearEl.textContent = new Date().getFullYear();
  refreshCartUI();
  loadCart();
  refreshWishUI();
  if (shopGrid) renderShop();
  renderRecent();
  watchImages();
  observeReveals();
  onScrollHeader();
  parallax();
})();
